<template>
  <div class="home">
    <!-- ═══════════════════════════ HERO ═══════════════════════════ -->
    <section class="hero">
      <div class="app-container hero__inner">
        <div class="hero__content">
          <span class="hero__eyebrow anim-fade-up">
            <span class="hero__pulse"></span>
            {{ $t('home.badge') }}
          </span>

          <h1 class="hero__title display-title anim-fade-up anim-delay-1">
            {{ $t('home.heroTitleA') }}<br />
            <span class="text-flame">{{ $t('home.heroTitleB') }}</span><br />
            {{ $t('home.heroTitleC') }}
          </h1>

          <p class="hero__subtitle anim-fade-up anim-delay-2">
            {{ $t('home.heroSubtitle') }}
          </p>

          <div class="hero__actions anim-fade-up anim-delay-3">
            <router-link :to="primaryCta" class="ui-btn ui-btn--primary ui-btn--lg">
              <i class="bi bi-lightning-charge-fill" aria-hidden="true"></i>
              {{ $t('home.ctaRegister') }}
            </router-link>
            <router-link to="/schedule" class="ui-btn ui-btn--secondary ui-btn--lg hover-arrow">
              {{ $t('home.ctaSchedule') }}
              <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════ TURNAMEN TERBUKA ═══════════════════ -->
    <section class="section">
      <div class="app-container">
        <header class="section__head section__head--row" v-reveal>
          <div>
            <span class="eyebrow">{{ $t('home.openEyebrow') }}</span>
            <h2 class="section-title">{{ $t('home.openTitle') }}</h2>
          </div>
          <router-link to="/detail" class="ui-btn ui-btn--ghost hover-arrow">
            {{ $t('home.allTournaments') }}
            <i class="bi bi-arrow-right" aria-hidden="true"></i>
          </router-link>
        </header>

        <div v-if="loading" class="card-grid">
          <div v-for="n in 3" :key="n" class="ui-card">
            <div class="ui-skeleton" style="aspect-ratio: 3/4"></div>
            <div class="ui-card__body stack">
              <div class="ui-skeleton" style="height: 22px; width: 80%"></div>
              <div class="ui-skeleton" style="height: 14px; width: 55%"></div>
            </div>
          </div>
        </div>

        <div v-else-if="error" class="ui-empty">
          <span class="ui-empty__icon"><i class="bi bi-wifi-off" aria-hidden="true"></i></span>
          <p class="ui-empty__title">{{ $t('home.loadFailed') }}</p>
          <p>{{ error }}</p>
          <button type="button" class="ui-btn ui-btn--secondary" style="margin-top: 1rem" @click="fetchData">
            <i class="bi bi-arrow-clockwise" aria-hidden="true"></i> {{ $t('common.retry') }}
          </button>
        </div>

        <div v-else-if="!tournaments.length" class="ui-empty">
          <span class="ui-empty__icon"><i class="bi bi-trophy" aria-hidden="true"></i></span>
          <p class="ui-empty__title">{{ $t('home.noOpenTournament') }}</p>
          <p>{{ $t('home.noOpenTournamentDesc') }}</p>
        </div>

        <!-- Jika hanya 1 turnamen: Tampilkan sebagai Featured Banner Card yang elegan dan proporsional -->
        <div v-else-if="tournaments.length === 1" class="t-single-wrapper" v-reveal>
          <article class="ui-card ui-card--flame t-card-single">
            <div class="t-card-single__poster">
              <router-link :to="`/detail/${tournaments[0].slug}`" class="hover-zoom">
                <SmartImage :source="tournaments[0].image" :alt="tournaments[0].name" :label="tournaments[0].name" ratio="3 / 4" />
                <span class="ui-badge t-card__badge" :class="`ui-badge--${infoStatus(tournaments[0]).tone}`">
                  {{ $t(infoStatus(tournaments[0]).kunci === 'status.dibuka' ? 'status.dibukaSingkat' : infoStatus(tournaments[0]).kunci) }}
                </span>
              </router-link>
            </div>

            <div class="t-card-single__content">
              <div class="t-card-single__badge-row">
                <span class="eyebrow"><i class="bi bi-fire text-flame"></i> {{ $t('home.openEyebrow') }}</span>
              </div>
              <h3 class="t-card-single__title">
                <router-link :to="`/detail/${tournaments[0].slug}`">{{ tournaments[0].name }}</router-link>
              </h3>

              <div class="t-card-single__meta-list">
                <div v-if="tournaments[0].location" class="t-card-single__meta-item">
                  <div class="t-card-single__meta-icon"><i class="bi bi-geo-alt-fill"></i></div>
                  <div>
                    <span class="t-card-single__meta-label">{{ $t('tournament.location') }}</span>
                    <span class="t-card-single__meta-val">{{ tournaments[0].location }}</span>
                  </div>
                </div>

                <div v-if="sisaPendaftaran(tournaments[0])" class="t-card-single__meta-item" :class="{ 'is-mendesak': mendesak(tournaments[0]) }">
                  <div class="t-card-single__meta-icon"><i class="bi bi-hourglass-split"></i></div>
                  <div>
                    <span class="t-card-single__meta-label">{{ $t('tournament.registrationDeadline') }}</span>
                    <span class="t-card-single__meta-val">{{ sisaPendaftaran(tournaments[0]) }}</span>
                  </div>
                </div>
              </div>

              <div class="t-card-single__actions">
                <router-link
                  v-if="tournaments[0].bisa_daftar || infoStatus(tournaments[0]).bisaDaftar"
                  :to="`/registration/${tournaments[0].slug}`"
                  class="ui-btn ui-btn--primary ui-btn--md"
                >
                  <i class="bi bi-pencil-square" aria-hidden="true"></i>
                  {{ $t('nav.register') }}
                </router-link>
                <router-link
                  :to="`/detail/${tournaments[0].slug}`"
                  class="ui-btn ui-btn--secondary ui-btn--md hover-arrow"
                >
                  {{ $t('home.viewEvent') }}
                  <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </router-link>
              </div>
            </div>
          </article>
        </div>

        <!-- Jika 2 turnamen atau lebih: Tampilkan dalam grid kartu reguler -->
        <div v-else class="card-grid">
          <article
            v-for="(t, i) in tournaments"
            :key="t.id"
            class="ui-card ui-card--interactive ui-card--flame t-card"
            v-reveal:[delayFor(i)]
          >
            <router-link :to="`/detail/${t.slug}`" class="t-card__link hover-zoom">
              <SmartImage :source="t.image" :alt="t.name" :label="t.name" ratio="3 / 4" />
              <span class="ui-badge t-card__badge" :class="`ui-badge--${infoStatus(t).tone}`">
                {{ $t(infoStatus(t).kunci === 'status.dibuka' ? 'status.dibukaSingkat' : infoStatus(t).kunci) }}
              </span>
            </router-link>

            <div class="ui-card__body">
              <h3 class="t-card__title">
                <router-link :to="`/detail/${t.slug}`">{{ t.name }}</router-link>
              </h3>
              <p v-if="t.location" class="t-card__meta">
                <i class="bi bi-geo-alt" aria-hidden="true"></i>{{ t.location }}
              </p>
              <p
                v-if="sisaPendaftaran(t)"
                class="t-card__meta t-card__tenggat"
                :class="{ 'is-mendesak': mendesak(t) }"
              >
                <i class="bi bi-hourglass-split" aria-hidden="true"></i>{{ sisaPendaftaran(t) }}
              </p>
            </div>

            <!--
              Satu tombol saja: pintu ke halaman detail.
              Kartu ini lebarnya hanya ~170px di layar 360px. Menaruh tombol
              daftar, jadwal, grup WhatsApp, dan proposal di sini membuat
              semuanya terpotong — dan tetap tidak cukup untuk memutuskan,
              karena biaya dan narahubung tiap acara berbeda. Semua tindakan
              itu ada di halaman detail, tempat ruangnya memadai.
            -->
            <div class="t-card__actions">
              <router-link
                v-if="t.bisa_daftar || infoStatus(t).bisaDaftar"
                :to="`/registration/${t.slug}`"
                class="ui-btn ui-btn--primary ui-btn--sm flex-grow-1"
              >
                <i class="bi bi-pencil-square" aria-hidden="true"></i>
                {{ $t('nav.register') }}
              </router-link>
              <router-link
                :to="`/detail/${t.slug}`"
                class="ui-btn ui-btn--secondary ui-btn--sm flex-grow-1"
              >
                {{ $t('home.viewEvent') }}
              </router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════ CTA ═══════════════════════ -->
    <!-- Ajakan kerjasama untuk penyelenggara (bukan untuk kontingen:
         kontingen sudah punya tombol daftar di hero & kartu turnamen). -->
    <section class="section cta-seksi">
      <div class="app-container">
        <div class="cta anim-ember" v-reveal="'scale'">
          <div class="cta__content">
            <span class="cta__eyebrow">{{ $t('home.ctaEyebrow') }}</span>
            <h2 class="cta__title">{{ $t('home.ctaTitle') }}</h2>
            <p class="cta__text">{{ $t('home.ctaText') }}</p>
          </div>
          <div class="cta__actions">
            <a :href="`https://wa.me/6283879795778?text=${encodeURIComponent($t('home.ctaWaPesan'))}`" target="_blank" rel="noopener noreferrer" class="ui-btn ui-btn--lg cta__btn-light">
              <i class="bi bi-whatsapp" aria-hidden="true"></i>
              {{ $t('home.ctaPrimary') }}
            </a>
            <router-link to="/about-us" class="ui-btn ui-btn--lg cta__btn-ghost">
              {{ $t('home.ctaSecondary') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import http from '@/services/http';
import { infoStatus, hanyaTerlihat } from '@/utils/status';
import SmartImage from '@/components/ui/SmartImage.vue';

export default {
  name: 'HomePage',

  components: { SmartImage },

  data() {
    return {
      tournaments: [],
      featured: null,
      loading: true,
      error: null,
    };
  },

  computed: {
    statusSorotan() {
      return infoStatus(this.featured);
    },

    /*
     * Tujuan tombol utama hero.
     * Kalau turnamen sorotan belum membuka pendaftaran, mengarahkan ke
     * formulir pendaftaran hanya membuat pengguna sampai di halaman yang
     * menolaknya. Lebih baik diarahkan ke halaman detail.
     */
    primaryCta() {
      /*
       * Tanpa acara sorotan, tombolnya mengarah ke pendaftaran biasa —
       * bukan ke halaman masuk. Pengunjung yang menekan "Daftar" ingin
       * membuat akun, bukan diminta masuk ke akun yang belum ada.
       */
      if (!this.featured) return '/registration';

      return this.statusSorotan.bisaDaftar
        ? `/registration/${this.featured.slug}`
        : `/detail/${this.featured.slug}`;
    },

  },

  mounted() {
    document.title = this.$t('common.appName') || 'Arena Sport Digital';
    this.fetchData();
  },

  methods: {
    infoStatus,

    delayFor(i) {
      return i * 80;
    },

    /** Hitung mundur pendaftaran, memakai angka yang dihitung server. */
    sisaPendaftaran(t) {
      // Saat pendaftaran tertutup (admin, waktu habis, kuota penuh) hitungan mundur tidak relevan.
      if (!infoStatus(t).bisaDaftar) return '';
      const n = t?.sisa_hari_pendaftaran;
      if (n === null || n === undefined) return '';
      if (n < 0) return this.$t('tournament.deadlinePassed');
      if (n === 0) return this.$t('tournament.deadlineToday');
      return this.$t('tournament.deadlineLeft', { n });
    },

    mendesak(t) {
      const n = t?.sisa_hari_pendaftaran;
      return typeof n === 'number' && n >= 0 && n <= 7;
    },

    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        /*
         * allSettled dipakai supaya kegagalan satu permintaan tidak
         * menjatuhkan yang lain. Endpoint highlight membalas 404 ketika
         * belum ada turnamen sorotan, dan dengan Promise.all biasa hal itu
         * ikut membatalkan daftar turnamen yang sebenarnya berhasil.
         */
        const [aktif, sorotan] = await Promise.allSettled([
          http.get('/tournaments/active'),
          http.get('/tournaments/highlight'),
        ]);

        if (aktif.status === 'fulfilled') {
          const d = aktif.value.data;
          // Backend sudah menyaring status "sembunyikan"; penyaringan di sini
          // adalah lapis kedua, untuk data yang mungkin datang dari endpoint
          // lama yang belum ikut diperbarui.
          this.tournaments = hanyaTerlihat(Array.isArray(d) ? d : d?.data ?? []);
        } else {
          throw aktif.reason;
        }

        this.featured =
          sorotan.status === 'fulfilled' ? sorotan.value.data : this.tournaments[0] ?? null;
      } catch (e) {
        this.error = e.friendlyMessage || this.$t('common.errorDesc');
        this.tournaments = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════ HERO ══════════════════════════ */
.hero {
  position: relative;
  overflow: hidden;
  padding-block: var(--space-10) var(--space-12);
  background: var(--hero-bg);
}

/*
 * `overflow: clip` menahan bola cahaya dekoratif di dalam hero.
 *
 * Kedua bola diletakkan menjorok keluar (right: -6%) dan masih digeser lagi
 * 30px oleh animasi. Tanpa pemotongan ini keduanya melebar melewati tepi
 * kanan layar dan menciptakan ruang gulir mendatar — halaman lalu bisa
 * digeser ke samping, dan seluruh isinya tampak meleset dari tengah.
 *
 * Dipakai `clip`, bukan `hidden`, agar tidak menjadi wadah gulir baru.
 */
.hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  overflow: clip;
}
.hero__grid { position: absolute; inset: 0; opacity: 0.5; }

.hero__orb--1 {
  width: min(460px, 90vw); height: min(460px, 90vw);
  top: -14%; right: -6%;
  background: radial-gradient(circle, var(--flame-500) 0%, transparent 70%);
  opacity: 0.2;
}

.hero__orb--2 {
  width: min(380px, 75vw); height: min(380px, 75vw);
  bottom: -18%; left: -8%;
  background: radial-gradient(circle, var(--gold-500) 0%, transparent 70%);
  opacity: 0.16;
  animation-delay: -7s;
}

.hero__inner {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-8);
  align-items: center;
}

@media (min-width: 768px) {
  .hero { padding-block: var(--space-16); }
}

@media (min-width: 992px) {
  .hero { padding-block: var(--space-20) var(--space-16); }
}

.hero__content {
  max-width: 780px;
  margin-inline: auto;
  text-align: center;
}

.hero__subtitle { margin-inline: auto; }
.hero__actions { display: flex; flex-wrap: wrap; gap: var(--space-3); justify-content: center; }

.hero__pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  margin-top: var(--space-6);
  justify-content: center;
}

.hero__pill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 6px 14px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-secondary);
  box-shadow: var(--shadow-xs);
}

.hero__pill i {
  font-size: 1rem;
}

.hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  margin-bottom: var(--space-5);
  max-width: 100%;
  text-align: left;
  line-height: 1.35;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-brand);
  background-color: var(--accent-subtle);
  border: 1px solid color-mix(in srgb, var(--accent) 22%, transparent);
  border-radius: var(--radius-full);
}

.hero__pulse {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--accent);
  animation: pulse-dot 1.8s ease-in-out infinite;
}

.hero__title {
  font-size: clamp(1.65rem, 6vw, 4.25rem);
  margin-bottom: var(--space-4);
}

.hero__subtitle {
  font-size: var(--text-base);
  color: var(--text-secondary);
  max-width: 48ch;
  margin-bottom: var(--space-6);
}

@media (min-width: 768px) {
  .hero__subtitle { font-size: var(--text-lg); margin-bottom: var(--space-8); }
}

/* ══════════════════════ KARTU TURNAMEN TUNGGAL ══════════════════════ */
.t-single-wrapper {
  max-width: 380px;
  margin-inline: auto;
}

.t-card-single {
  display: grid;
  grid-template-columns: 1fr;
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-2xl);
  background: var(--bg-surface);
  box-shadow: var(--shadow-md);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.t-card-single:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.t-card-single__poster {
  position: relative;
  overflow: hidden;
}

.t-card-single__content {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.t-card-single__title {
  font-size: var(--text-lg, 1.125rem);
  margin: 0;
  font-weight: 700;
  line-height: 1.3;
}

.t-card-single__title a {
  color: var(--text-primary);
  text-decoration: none;
}

.t-card-single__title a:hover {
  color: var(--text-brand);
}

.t-card-single__meta-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.t-card-single__meta-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-sm);
}

.t-card-single__meta-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.t-card-single__meta-label {
  display: block;
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.t-card-single__meta-val {
  display: block;
  font-weight: 600;
  color: var(--text-primary);
}

.t-card-single__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
  margin-top: var(--space-1);
}
.t-card-single__actions .ui-btn { justify-content: center; font-size: var(--text-sm); padding-inline: var(--space-3); white-space: nowrap; }
.t-card-single__actions .ui-btn:only-child { grid-column: 1 / -1; }
.t-card-single__badge-row .eyebrow { font-size: 0.7rem; }
.t-card-single__meta-list { gap: var(--space-2); }
.t-card-single__meta-icon { width: 30px !important; height: 30px !important; font-size: 0.95rem !important; }

/* Layar laptop/desktop: poster di kiri, info di kanan, tinggi poster mengikuti
   tinggi layar supaya seluruh kartu terlihat tanpa menggulir pada zoom 100%. */
@media (min-width: 768px) {
  .t-single-wrapper { max-width: 720px; }
  .t-card-single { grid-template-columns: auto 1fr; align-items: stretch; }
  .t-card-single__poster { width: clamp(220px, calc((100vh - 200px) * 0.75), 320px); }
  .t-card-single__content { padding: var(--space-6); justify-content: center; gap: var(--space-4); }
  .t-card-single__title { font-size: var(--text-2xl, 1.5rem); }
  .t-card-single__badge-row .eyebrow { font-size: 0.75rem; }
}

/* ══════════════════════ KARTU TURNAMEN ══════════════════════ */
.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-3);
}

@media (min-width: 560px)  { .card-grid { gap: var(--space-5); } }
@media (min-width: 900px)  { .card-grid { grid-template-columns: repeat(3, 1fr); gap: var(--space-6); } }
@media (min-width: 1200px) { .card-grid { grid-template-columns: repeat(4, 1fr); } }

.t-card { display: flex; flex-direction: column; }
.t-card__link { position: relative; display: block; }

.t-card__badge {
  position: absolute;
  /* Di sudut BAWAH: bagian atas poster memuat logo penyelenggara dan judul
     acara, sedangkan sudut bawah umumnya hanya berisi latar. */
  top: var(--space-3);
  right: var(--space-3);
  z-index: 2;
  background: var(--bg-surface);
  box-shadow: var(--shadow-md);
}

.t-card .ui-card__body { flex: 1; }
.t-card__title {
  font-size: var(--text-base);
  margin: 0 0 var(--space-2);
  overflow-wrap: anywhere;
}

@media (min-width: 768px) { .t-card__title { font-size: var(--text-lg); } }

.t-card__title a { color: var(--text-primary); }
.t-card__title a:hover { color: var(--text-brand); }

.t-card__meta {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: 1.4;
}

.t-card__meta i { margin-top: 2px; flex-shrink: 0; }

@media (min-width: 768px) { .t-card__meta { font-size: var(--text-sm); } }

.t-card__meta i { color: var(--accent); }

/*
 * Warna WhatsApp dipakai apa adanya (#25D366) supaya tombolnya langsung
 * dikenali. Teksnya dipaksa gelap, bukan mengikuti --text-primary: di mode
 * gelap variabel itu bernilai terang, dan putih di atas hijau muda nyaris
 * tidak terbaca.
 */
.detail__wa {
  background: #25d366;
  color: #04310f;
  border: 1px solid #1da851;
}
.detail__wa:hover { background: #1fbe5a; color: #04310f; }

.t-card__tenggat { color: var(--text-muted); }
.t-card__tenggat.is-mendesak { color: var(--danger-600); font-weight: 700; }

.t-card__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--border-subtle);
}

.t-card__actions .ui-btn {
  width: 100%;
  min-height: 34px;
  padding: 6px 8px;
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .t-card__actions {
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-5);
  }
  .t-card__actions .ui-btn {
    flex: 1;
    min-width: 0;
    font-size: var(--text-sm);
    min-height: 38px;
  }
}

/* ══════════════════════ CTA ══════════════════════ */
/* Pita ajakan: rapat, teks kiri + tombol kanan di layar lebar. */
.cta-seksi { padding-top: 0; }
.cta {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  text-align: center;
  padding: var(--space-6) var(--space-5);
  border-radius: var(--radius-2xl);
  background: var(--grad-flame);
  color: #fff;
  box-shadow: 0 16px 40px -14px rgba(235, 94, 40, 0.35);
}

@media (min-width: 900px) {
  .cta {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-8);
    text-align: left;
    padding: var(--space-8) var(--space-10);
  }
  .cta__content { max-width: 62ch; }
  .cta__actions { justify-content: flex-end; flex-shrink: 0; }
  .cta__text { margin-inline: 0 !important; }
}

.cta__glow {
  position: absolute;
  top: -50%; left: 50%;
  width: 620px; height: 620px;
  transform: translateX(-50%);
  background: radial-gradient(circle, rgb(255 255 255 / 0.22) 0%, transparent 62%);
  pointer-events: none;
}

.cta__content { position: relative; }

.cta__eyebrow {
  display: inline-block;
  margin-bottom: var(--space-2);
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: rgb(255 255 255 / 0.85);
}

.cta__title {
  color: #fff;
  font-family: var(--font-display);
  font-size: clamp(1.25rem, 3.2vw, 1.85rem);
  line-height: 1.2;
  margin: 0 0 var(--space-2);
}

.cta__text {
  color: rgb(255 255 255 / 0.92);
  font-size: var(--text-base);
  line-height: 1.55;
  max-width: 60ch;
  margin: 0 auto;
}

.cta__actions { display: flex; flex-wrap: wrap; gap: var(--space-3); justify-content: center; }

/* Sengaja putih, bukan token: tombol ini berdiri di atas blok gradasi
   oranye pekat yang warnanya sama di mode terang maupun gelap. */
.cta__btn-light { background: #fff; color: var(--flame-800); }
.cta__btn-light:hover { background: var(--warm-100); color: var(--flame-900); }

.cta__btn-ghost {
  background: rgb(255 255 255 / 0.12);
  color: #fff;
  border-color: rgb(255 255 255 / 0.35);
}
.cta__btn-ghost:hover { background: rgb(255 255 255 / 0.2); color: #fff; }
</style>
